import { CheckCircle2, Clock3, Copy } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { fmtDate, fmtMoney } from '@/features/shared/format'
import { copyWithToast } from '../../lib/exportActions'
import type { OdemeView } from '../../api'
import { OdemeStatusPill } from '../StatusPill'
import { OdemeRowActions } from './OdemeRowActions'

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  )
}

export function OdemeDetailCard({ odeme }: { odeme: OdemeView }) {
  const { t } = useTranslation()
  const iban = odeme.iban || '—'
  const paid = odeme.durum === 'odendi'

  return (
    <Card className="rounded-2xl border-border/70 shadow-sm">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 border-b pb-4">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={odeme.ad} className="size-9 sm:size-11" />
          <div className="min-w-0">
            <CardTitle className="truncate text-base">{odeme.ad}</CardTitle>
            <div className="truncate text-xs text-muted-foreground">
              {[odeme.unvan, odeme.birim].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
        <OdemeRowActions odeme={odeme} variant="button" />
      </CardHeader>
      <CardContent className="pt-4">
        <Tabs defaultValue="detay">
          <TabsList>
            <TabsTrigger value="detay">{t('hr.personel.odemeler.tabs.detay')}</TabsTrigger>
            <TabsTrigger value="banka">{t('hr.personel.odemeler.tabs.banka')}</TabsTrigger>
            <TabsTrigger value="onay">{t('hr.personel.odemeler.tabs.onay')}</TabsTrigger>
            <TabsTrigger value="mutabakat">{t('hr.personel.odemeler.tabs.mutabakat')}</TabsTrigger>
          </TabsList>
          <TabsContent value="detay" className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <Info label={t('hr.personel.odemeGecmis.tur')}>{t(`hr.personel.odeme.tur.${odeme.tur}`)}</Info>
            <Info label={t('hr.personel.ucret.donem')}>{odeme.donem}</Info>
            <Info label={t('hr.personel.odemeGecmis.tutar')}>
              <span className="font-semibold tabular-nums">{fmtMoney(odeme.tutar)}</span>
            </Info>
            <Info label={t('hr.personel.columns.durum')}>
              <OdemeStatusPill durum={odeme.durum} />
            </Info>
          </TabsContent>
          <TabsContent value="banka" className="mt-4 text-sm">
            <div className="rounded-xl border p-4">
              <div className="font-medium">{odeme.banka || t('hr.personel.odemeler.bankaYok')}</div>
              <div className="mt-1 font-mono text-xs break-all text-muted-foreground">{iban}</div>
              <div className="mt-2 text-muted-foreground">{odeme.ad}</div>
              {/* IBAN listede maskeli gelir; açık IBAN yalnız sunucuda üretilen banka dosyasında yer alır. */}
              {iban !== '—' && !iban.includes('*') ? (
                <Button variant="outline" size="sm" className="mt-3" onClick={() => void copyWithToast(iban.replace(/\s/g, ''), 'hr.personel.actions.odeme.ibanCopied')}>
                  <Copy />
                  {t('hr.personel.odemeler.kopyala')}
                </Button>
              ) : null}
            </div>
          </TabsContent>
          <TabsContent value="onay" className="mt-4 space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-success" />
              <span>{t('hr.personel.actions.odeme.islem.created')}</span>
            </div>
            <div className="flex items-center gap-2">
              {odeme.onaylandi ? <CheckCircle2 className="size-4 text-success" /> : <Clock3 className={odeme.reddedildi ? 'size-4 text-destructive' : 'size-4 text-warning'} />}
              <span className={odeme.onaylandi ? undefined : 'text-muted-foreground'}>
                {t(odeme.reddedildi ? 'hr.personel.actions.odeme.islem.rejected' : 'hr.personel.actions.odeme.islem.approved')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {paid ? <CheckCircle2 className="size-4 text-success" /> : <Clock3 className="size-4 text-muted-foreground" />}
              <span className={paid ? undefined : 'text-muted-foreground'}>
                {t('hr.personel.actions.odeme.islem.paid')}
                {paid && odeme.tarih !== '—' ? ` · ${fmtDate(odeme.tarih)}` : ''}
              </span>
            </div>
          </TabsContent>
          <TabsContent value="mutabakat" className="mt-4">
            {paid ? (
              <div className="flex items-center gap-2 rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
                <CheckCircle2 className="size-4" />
                {t('hr.personel.odemeler.mutabakatOk')}
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
                <Clock3 className="size-4" />
                {t(`hr.personel.odeme.durum.${odeme.durum}`)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
